import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ShieldCheck, ArrowRight, Star } from "lucide-react";
import { myPeerReviewStats } from "@/lib/apiClient";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Community card — drop-in tile that surfaces the current learner's
 * peer-review points, "peer-reviewed" badges and open submissions.
 * Used by both the dashboard and the profile/settings page.
 */
export default function CommunityCard({ compact = false }) {
  const { t } = useI18n();
  const [peer, setPeer] = useState(null);

  useEffect(() => { myPeerReviewStats().then(setPeer).catch(() => {}); }, []);

  if (!peer) return null;
  const points = peer.peer_review_points || 0;
  const badges = peer.my_badged_recitations || 0;
  const open = peer.my_open_submissions || 0;

  return (
    <section
      data-testid="community-card"
      className={`rounded-3xl border border-[#FED7AA] bg-gradient-to-br from-[#FFF7ED] to-[#FFFBEB] ${compact ? "p-5" : "p-6 sm:p-8"}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-[#92400E]">{t("community.eyebrow")}</div>
          <h3 className="font-heading text-xl sm:text-2xl text-[#1F2937] mt-1.5 inline-flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D97706]" /> {t("community.title")}
          </h3>
          <p className="text-sm text-[#4B5563] mt-1.5 max-w-md">{t("community.body")}</p>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3">
        <Stat icon={<Star className="w-4 h-4" />} label={t("community.points")} value={points} testid="community-card-points" />
        <Stat icon={<ShieldCheck className="w-4 h-4" />} label={t("community.badges")} value={badges} testid="community-card-badges" />
        <Stat label={t("community.in_queue")} value={open} testid="community-card-open" />
      </div>
      <div className="mt-5">
        <Link
          to="/community?tab=peer"
          data-testid="community-card-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D97706] hover:bg-[#B45309] text-white text-sm font-medium transition">
          {t("community.review_cta")} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white border border-[#FED7AA] px-3.5 py-3">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[#92400E] inline-flex items-center gap-1.5">
        {icon}{label}
      </div>
      <div className="font-heading text-2xl text-[#1F2937] mt-1">{value}</div>
    </div>
  );
}
